/**
 * Helper functions for post management
 */

/**
 * Convert camelCase string to readable format
 * Examples:
 * - "insightReport" -> "Insight Report"
 * - "caseStudy" -> "Case Study"
 * - "educationalPost" -> "Educational Post"
 * - "newsCommentary" -> "News Commentary"
 */
export const formatTemplateName = (templateName: string): string => {
  if (!templateName) return '';

  // Handle already formatted names (with spaces)
  if (templateName.includes(' ')) {
    return templateName;
  }

  // Insert space before uppercase letters and convert to title case
  const formatted = templateName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return formatted;
};

/**
 * Compare two dates (handles null/undefined)
 */
export const datesAreEqual = (date1: Date | null, date2: Date | null): boolean => {
  if (date1 === null && date2 === null) return true;
  if (date1 === null || date2 === null) return false;
  return date1.toISOString().split('T')[0] === date2.toISOString().split('T')[0];
};

/**
 * Build render payload for post rendering
 */
export const buildRenderPayload = (data: {
  templateName?: string;
  contentType?: string;
  bullets?: string[];
  title?: string;
  subtitle?: string;
  mainText?: string;
  logo?: string;
  photo?: string;
  background?: string;
  image1?: string;
  image2?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  fontForText?: string;
  imageGeneratedTopic?: string;
  cta?: string;
  date?: string;
  time?: string;
  speakerPhoto?: string;
  speakerRole?: string;
  phone?: string;
  website?: string;
  backgroundColor?: string;
  brandHandle?: string;
  brandName?: string;
  versionId?: number;
  postId?: string;
}) => {
  const payload: any = {};

  if (data.templateName) payload.templateName = data.templateName;
  if (data.contentType) payload.contentType = data.contentType;
  if (data.versionId) payload.versionId = data.versionId;
  if (data.postId) payload.postId = data.postId;

  if (data.bullets && data.bullets.length > 0) {
    payload.bullets = data.bullets.filter((b) => b.trim() !== '');
  }

  // Always include these fields explicitly
  payload.title = data.title || '';
  payload.subtitle = data.subtitle || '';
  payload.mainText = data.mainText || '';
  payload.logo = data.logo || '';
  payload.photo = data.photo || '';
  payload.background = data.background || '';
  payload.image1 = data.image1 || '';
  payload.image2 = data.image2 || '';
  payload.color1 = data.color1 || '#e92d4b';
  payload.color2 = data.color2 || '#ffffff';
  payload.color3 = data.color3 || '#000000';
  payload.fontForText = data.fontForText || '';
  payload.imageGeneratedTopic = data.imageGeneratedTopic || '';
  payload.cta = data.cta || '';

  if (data.date) payload.date = data.date;
  if (data.time) payload.time = data.time;
  if (data.speakerPhoto) payload.speakerPhoto = data.speakerPhoto;
  if (data.speakerRole) payload.speakerRole = data.speakerRole;
  if (data.phone) payload.phone = data.phone;
  if (data.website) payload.website = data.website;
  if (data.backgroundColor) payload.backgroundColor = data.backgroundColor;
  if (data.brandHandle) payload.brandHandle = data.brandHandle;
  if (data.brandName) payload.brandName = data.brandName;

  return payload;
};

/**
 * Build post text for LinkedIn publishing
 */
export const buildPostText = (data: {
  title?: string;
  subtitle?: string;
  mainText?: string;
  cta?: string;
}): string => {
  const postTextParts: string[] = [];

  if (data.title) postTextParts.push(data.title);
  if (data.subtitle) postTextParts.push(data.subtitle);
  if (data.mainText) postTextParts.push(data.mainText);
  if (data.cta) postTextParts.push(`\n${data.cta}`);

  const postText = postTextParts.join('\n\n');
  return postText.trim() || data.title || '';
};

/**
 * Build save payload for post updates
 */
export const buildSavePayload = (data: {
  title: string;
  subtitle: string;
  mainText: string;
  logo: string;
  photo: string;
  background: string;
  image1: string;
  image2: string;
  color1: string;
  color2: string;
  color3: string;
  fontForText: string;
  imageGeneratedTopic: string;
  cta: string;
  bullets: string[];
  date: string;
  time: string;
  speakerPhoto: string;
  speakerRole: string;
  phone: string;
  website: string;
  backgroundColor: string;
  brandHandle: string;
  brandName: string;
  plannedDate: Date | null;
  templateName: string;
}) => {
  return {
    title: data.title,
    subtitle: data.subtitle,
    mainText: data.mainText,
    logo: data.logo,
    photo: data.photo,
    background: data.background,
    image1: data.image1,
    image2: data.image2,
    color1: data.color1,
    color2: data.color2,
    color3: data.color3,
    fontForText: data.fontForText,
    imageGeneratedTopic: data.imageGeneratedTopic,
    cta: data.cta,
    bullets: data.bullets.filter((b) => b.trim() !== ''),
    date: data.date || undefined,
    time: data.time || undefined,
    speakerPhoto: data.speakerPhoto || undefined,
    speakerRole: data.speakerRole || undefined,
    phone: data.phone || undefined,
    website: data.website || undefined,
    backgroundColor: data.backgroundColor || undefined,
    brandHandle: data.brandHandle || undefined,
    brandName: data.brandName || undefined,
    // Send date as YYYY-MM-DD string to avoid timezone conversion issues
    // The backend will handle timezone conversion based on user's timezone
    plannedDate: data.plannedDate
      ? (() => {
        const year = data.plannedDate.getFullYear();
        const month = String(data.plannedDate.getMonth() + 1).padStart(2, '0');
        const day = String(data.plannedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })()
      : undefined,
    templateName: data.templateName || undefined,
  };
};

